import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Dialog, DialogTitle, DialogContent, DialogActions, Stack, Table as MuiTable, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import { TableCell } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import AdminWPListTableUser from "../list/AdminWPListTableUser";
import allCommonCodeMap from "api/commonCode/allCommonCodeMap";
import { AmountFormat } from "utils/AmountFormat";
import wpAddPointList from "api/wp/wpAddPointList";
import Pagination from "components/pagination/Pagination";
import moment from 'moment'
import { customAxios } from "../../../../axios/CustomAxios";
import { myIpAddress } from "config";

const AdditionalPointSetting = () => {
  const { light } = colors;
  const { borderWidth } = borders;
  const queryClient = useQueryClient();

  // 상태 코드 → 한글 라벨 매핑
  const STATUS_LABELS = {
    PENDING: "예정",
    ACTIVE: "진행중",
    COMPLETED: "완료",
    CANCELED: "취소",
    // 서버 정규화 대비(이전 명칭 호환)
    SCHEDULED: "예정",
    IN_PROGRESS: "진행중",
  };

  // 공통 코드 (부서/직급 라벨용)
  const { data: codeMap } = useQuery("allCommonCodeMap", allCommonCodeMap, {
    select: (res) => res.data,
  });

  // 날짜/연도
  const todayDate = new Date();
  const year = todayDate.getFullYear().toString();

  // 이력 테이블
  const historyColumns = useMemo(
    () => [
      { key: "startYearMonth", name: "지급 시작일(적용 일)", align: "center", width: "15%", sortable: true },
      { key: "user", name: "사원", align: "left", width: "10%", sortable: true },
      { key: "amount", name: "금액", align: "center", width: "10%" },
      { key: "months", name: "분할개월", align: "center", width: "10%" },
      { key: "paidPoint", name: "현재 지급된 금액", align: "center", width: "10%" },
      { key: "status", name: "진행 상태", align: "center", width: "11%" },
      { key: "reason", name: "사유", align: "left", width: "16%" },
      { key: "createDate", name: "등록시각", align: "center", width: "18%", sortable: true },
    ],
    []
  );

  const [historySearch, setHistorySearch] = useState("");

  // 정렬 상태
  const [orderBy, setOrderBy] = useState("createDate"); // 기본: 등록시각
  const [order, setOrder] = useState("desc"); // desc: 최신 우선

  // 페이지네이션 상태 (신청 목록과 동일한 하단 페이징 컴포넌트 사용)
  // page: 1부터 시작, pageSize: 페이지 당 행 수
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 서버 데이터 조회 (/wp/v2/addPoint/list)
  const { data: apiData, isLoading, isError, error } = useQuery(
    [
      "wpAddPointList",
      { year, keyword: historySearch, orderBy, order, page, pageSize },
    ],
    () => wpAddPointList({ year, keyword: historySearch, orderBy, order, page, pageSize }),
    {
      select: (res) => {
        const raw = res?.data ?? {};
        const list = Array.isArray(raw?.content)
          ? raw?.content
          : [];
        const totalCount =
          typeof raw.totalElements === "number"
            ? raw.totalElements
            : list.length;

        // 서버 응답을 화면 스키마로 매핑
        const normalized = list.map((v, idx) => {
          const id = v.no;
          const mem_no = v.mem_no ?? v.memberNo ?? v.userId ?? null;
          const mem_name = v.mem_name ?? v.memberName ?? v.userName ?? "";
          const mem_team = v.mem_team ?? v.teamCode ?? v.team ?? null;
          const mem_rank = v.mem_rank ?? v.rankCode ?? v.rank ?? null;
          const imageUrl = v.imageUrl ?? v.profileImageUrl ?? v.avatarUrl ?? "";
          const amount =
            v.additional_point ??  0;
          const paid_amount = v.paidPoint ?? 0;
          const months =
            v.months ?? v.distributionMonths ?? v.monthlyMonths ?? 0;
          const reason = v.reason ?? v.memo ?? "";
          const applyDate = v.startYearMonth ?? null;
          const createdAt = v.createDate?? new Date().toISOString();
          // 지급 상태 정규화 (서버의 다양한 키 수용)
          const status =
            v.status || v.state || v.progress || v.paymentStatus || "SCHEDULED";
          return {
            id,
            mem_no,
            mem_name,
            mem_team,
            mem_rank,
            imageUrl,
            amount,
            months,
              paid_amount,
            reason,
            applyDate,
            createdAt,
            status,
          };
        });
        return { list: normalized, totalCount };
      },
      keepPreviousData: true,
    }
  );

  const history = apiData?.list || [];
  const totalCount = apiData?.totalCount || 0;

  const handleRequestSort = (key) => {
    if (orderBy === key) {
      // 같은 컬럼 재클릭 시 방향 토글
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(key);
      // 초깃값: 날짜 계열은 desc, 사용자(문자열)는 asc
      if (key === "user") setOrder("asc");
      else setOrder("desc");
    }
    // 정렬 변경 시 첫 페이지로 이동
    setPage(1);
  };

  // 신청 목록의 커스텀 페이징은 페이지 크기 선택 UI가 별도로 없으므로
  // pageSize는 고정 값(기본 10)으로 유지합니다. 필요 시 상단에서 상태 변경 UI를 추가하세요.

  // 행 클릭 시 수정 다이얼로그 오픈 상태 및 폼
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [form, setForm] = useState({ amount: "", reason: "", months: "", applyDate: "", status: "SCHEDULED" });

  const handleRowClick = (row) => {
    setSelectedRow(row);
    setForm({
      amount: row?.amount ?? "",
      reason: row?.reason ?? "",
      months: row?.months ?? "",
      // month input용 값(YYYY-MM)
      applyDate: row?.applyDate ? moment(row.applyDate).format("YYYY-MM") : "",
      status: row?.status || "SCHEDULED",
    });
    setOpenEdit(true);
  };
  const handleClose = () => {
    setOpenEdit(false);
    setSelectedRow(null);
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // 수정 저장 뮤테이션 (API 확정 전 임시 엔드포인트 가정)
  const updateMutation = useMutation(
    (payload) => customAxios.post(myIpAddress + "/wp/v2/addPoint/update", payload),
    {
      onSuccess: () => {
        // 목록 재조회
        queryClient.invalidateQueries({ queryKey: ["wpAddPointList"], exact: false });
        alert("수정이 완료되었습니다.");
        handleClose();
      },
      onError: (err) => {
        // eslint-disable-next-line no-console
        console.error("[addPoint update]", err);
        alert("수정 처리에 실패했습니다.");
      },
    }
  );

  const handleSave = () => {
    const amountNum = parseInt(form.amount, 10);
    if (isNaN(amountNum) || amountNum <= 0) {
      alert("금액을 1원 이상 정수로 입력하세요.");
      return;
    }
    const monthsNum = form.months === "" ? 0 : parseInt(form.months, 10);
    if (isNaN(monthsNum) || monthsNum < 0) {
      alert("분할 개월은 0 이상의 정수로 입력하세요.");
      return;
    }

    const payload = {
        addPoint_no: selectedRow?.id,
        mem_no: selectedRow?.mem_no,
        additional_point: amountNum,
        distributionMonths: monthsNum,
        reason: form.reason || '',
        // 서버에서 startYearMonth를 기대한다고 가정
        startYearMonth: form.applyDate ? `${form.applyDate}-01` : null,
        status: form.status,
    }

    // TODO: 서버 스펙에 맞게 메서드/경로/파라미터 수정 필요
    updateMutation.mutate(payload);
  };

  return (
    <SoftBox p={2}>
      <SoftTypography variant="h6" fontWeight="bold" mb={1}>
        추가 지급 이력/현황
      </SoftTypography>
      <SoftTypography variant="button" color="text" mb={2}>
        사용자 설정 팝업에서 "추가 지급" 탭으로 저장한 내용이 여기 이력으로 표시됩니다.
      </SoftTypography>

      <SoftBox mb={2} display="flex" alignItems="center" gap={1}>
        <SoftInput
          placeholder="이름/부서/직급/사유 검색"
          value={historySearch}
          onChange={(e) => setHistorySearch(e.target.value)}
          style={{ maxWidth: 360 }}
        />
        <SoftTypography variant="caption" color="text">
            결과 {totalCount}건
        </SoftTypography>
      </SoftBox>

      <TableContainer sx={{ maxHeight: 450 }}>
        <MuiTable stickyHeader>
          <thead>
            <TableRow>
              {historyColumns.map((c) => {
                const isActive = c.sortable && (orderBy === c.key);
                const arrow = isActive ? (order === "asc" ? "▲" : "▼") : "";
                return (
                  <TableCell
                    key={c.key}
                    align={c.align}
                    sx={{
                      width: c.width,
                      borderBottom: `${borderWidth[1]} solid ${light.main}`,
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      backgroundColor: ({ palette }) => palette.background.paper,
                      cursor: c.sortable ? "pointer" : "default",
                    }}
                    onClick={c.sortable ? () => handleRequestSort(c.key) : undefined}
                  >
                    <SoftTypography variant="button" color={isActive ? "dark" : "text"} fontWeight="bold">
                      {c.name} {arrow}
                    </SoftTypography>
                  </TableCell>
                );
              })}
            </TableRow>
          </thead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={historyColumns.length} align="center">
                  <SoftTypography variant="button" color="text">로딩 중...</SoftTypography>
                </TableCell>
              </TableRow>
            )}

            {!isLoading && isError && (
              <TableRow>
                <TableCell colSpan={historyColumns.length} align="center">
                  <SoftTypography variant="button" color="text">오류가 발생했습니다.</SoftTypography>
                </TableCell>
              </TableRow>
            )}

            {!isLoading && !isError && history.length === 0 && (
              <TableRow>
                <TableCell colSpan={historyColumns.length} align="center">
                  <SoftTypography variant="button" color="text">표시할 이력이 없습니다.</SoftTypography>
                </TableCell>
              </TableRow>
            )}

            {!isLoading && !isError && history.map((h) => {
              const team = h["mem_team"] && codeMap ? codeMap[h["mem_team"]]?.info : "";
              const rank = h["mem_rank"] && codeMap ? codeMap[h["mem_rank"]]?.info : "";
              const amount = AmountFormat(parseInt(h.amount));
              const paid_amount = AmountFormat(parseInt(h.paid_amount));
              const months = h.months || 0;
              const apply = h.applyDate ? new Date(h.applyDate).toLocaleDateString("ko-KR") : "즉시";
              const created = moment(h.createdAt).format("YYYY-MM-DD HH:mm");
              return (
                <TableRow key={h.id} hover sx={{ cursor: "pointer" }} onClick={() => handleRowClick(h)}>
                  <TableCell align="center" style={{ borderBottom: `${borderWidth[1]} solid ${light.main}` }}>
                    <SoftTypography variant="button" color="dark">{apply}</SoftTypography>
                  </TableCell>
                  <TableCell align="left" style={{ borderBottom: `${borderWidth[1]} solid ${light.main}` }}>
                    <AdminWPListTableUser name={h.mem_name} team={team} rank={rank} imageUrl={h.imageUrl} />
                  </TableCell>
                  <TableCell align="center" style={{ borderBottom: `${borderWidth[1]} solid ${light.main}` }}>
                    <SoftTypography variant="button" color="dark">{amount} 원</SoftTypography>
                  </TableCell>
                  <TableCell align="center" style={{ borderBottom: `${borderWidth[1]} solid ${light.main}` }}>
                    <SoftTypography variant="button" color="dark">{months}</SoftTypography>
                  </TableCell>
                    <TableCell align="center" style={{ borderBottom: `${borderWidth[1]} solid ${light.main}` }}>
                        <SoftTypography variant="button" color="dark">{paid_amount} 원</SoftTypography>
                    </TableCell>
                    <TableCell align="center" style={{ borderBottom: `${borderWidth[1]} solid ${light.main}` }}>
                    <SoftTypography variant="button" color="dark">{STATUS_LABELS[h.status] || h.status}</SoftTypography>
                  </TableCell>
                  <TableCell align="left" style={{ borderBottom: `${borderWidth[1]} solid ${light.main}` }}>
                    <SoftTypography variant="button" color="dark">{h.reason || "-"}</SoftTypography>
                  </TableCell>
                  <TableCell align="center" style={{ borderBottom: `${borderWidth[1]} solid ${light.main}` }}>
                    <SoftTypography variant="button" color="dark">{created}</SoftTypography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </MuiTable>
      </TableContainer>

      {/* 행 클릭 시 수정 팝업 */}
      <Dialog open={openEdit} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <SoftTypography variant="h6" fontWeight="bold">
            추가 복지포인트 수정: {selectedRow?.mem_name || ""}
          </SoftTypography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <SoftBox>
              <SoftTypography variant="caption" color="text" mb={0.5} display="block">
                금액(원)
              </SoftTypography>
              <SoftInput type="number" name="amount" value={form.amount} onChange={handleChange} placeholder="예: 10000" />
            </SoftBox>
            <SoftBox>
              <SoftTypography variant="caption" color="text" mb={0.5} display="block">
                사유(선택)
              </SoftTypography>
              <SoftInput name="reason" value={form.reason} onChange={handleChange} placeholder="사유를 입력하세요" />
            </SoftBox>
            <SoftBox>
              <SoftTypography variant="caption" color="text" mb={0.5} display="block">
                분할 지급 개월(선택)
              </SoftTypography>
              <SoftInput type="number" name="months" value={form.months} onChange={handleChange} placeholder="예: 3 (0 또는 미입력 시 일시 지급)" />
            </SoftBox>
            <SoftBox>
              <SoftTypography variant="caption" color="text" mb={0.5} display="block">
                적용 시점(선택)
              </SoftTypography>
              <SoftInput type="month" name="applyDate" value={form.applyDate} onChange={handleChange} placeholder="미입력 시 즉시 적용" />
            </SoftBox>
            <SoftBox>
              <SoftTypography variant="caption" color="text" mb={0.5} display="block">
                지급 상태
              </SoftTypography>
              <FormControl fullWidth size="small">
                <Select
                  labelId="addpoint-status-label"
                  id="addpoint-status"
                  name="status"
                  value={form.status}
                  label="지급 상태"
                  onChange={handleChange}
                >
                  <MenuItem value="PENDING">예정</MenuItem>
                  <MenuItem value="ACTIVE">진행중</MenuItem>
                  <MenuItem value="COMPLETED">완료</MenuItem>
                  <MenuItem value="CANCELED">취소</MenuItem>
                </Select>
              </FormControl>
            </SoftBox>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SoftButton variant="outlined" color="secondary" onClick={handleClose}>취소</SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleSave} disabled={updateMutation.isLoading}>
            {updateMutation.isLoading ? "저장 중..." : "저장"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* 사용자 복지포인트 신청 목록과 동일한 최하단 페이징 적용 */}
      <SoftBox pt={2} pb={1} display="flex" justifyContent="center">
        <Pagination
          totalCount={totalCount}
          pageSize={pageSize}
          page={page}
          setPage={setPage}
          size="small"
        />
      </SoftBox>
    </SoftBox>
  );
};

export default AdditionalPointSetting;
